import { doc, getDoc, type Firestore } from 'firebase/firestore'
import * as firebaseServices from './firebase'
import { switchedOffModulesFor, type WorkspaceModuleSource } from '../utils/workspaceModules'
import type { MemberControls } from '../types/memberControls'

export interface FunPackMemberTarget {
  role?: string | null
  effectiveControls?: MemberControls | null
}

/**
 * Client helper to evaluate whether fun pack features are effectively enabled
 * for a specific person in a workspace (F-TT3afix3-2, mirrors functions-common isFunPackEffectivelyEnabled).
 *
 * Rules:
 * - If workspace has funPack module switched off: disabled for everyone.
 * - Controls never apply to owners (isOwner === true or role === 'OWNER'): enabled.
 * - If person has effectiveControls.funPack === 'OFF': disabled for this member.
 * - Otherwise (INHERIT or undefined): enabled.
 */
export function isFunPackEffectivelyEnabled(
  workspace: WorkspaceModuleSource | null | undefined,
  person?: FunPackMemberTarget | null,
  isOwner?: boolean,
): boolean {
  if (workspace && switchedOffModulesFor(workspace).has('funPack')) {
    return false
  }
  if (isOwner === true || person?.role === 'OWNER') {
    return true
  }
  if (person?.effectiveControls?.funPack === 'OFF') {
    return false
  }
  return true
}

/**
 * Async helper to evaluate whether fun pack features are effectively enabled
 * for the given actor in a workspace.
 */
export async function isFunPackEnabledForActor(
  workspaceId: string,
  actorId: string,
): Promise<boolean> {
  if (!workspaceId || !actorId) return false
  try {
    const firestoreDb = (firebaseServices as unknown as { firestoreDb: Firestore }).firestoreDb
    if (!firestoreDb) return true

    const wsSnap = await getDoc(doc(firestoreDb, 'workspaces', workspaceId))
    const wsData = wsSnap.exists() ? (wsSnap.data() as WorkspaceModuleSource) : null
    if (wsData && switchedOffModulesFor(wsData).has('funPack')) {
      return false
    }

    const membershipSnap = await getDoc(
      doc(firestoreDb, 'users', actorId, 'workspaceMemberships', workspaceId),
    )
    const membershipData = membershipSnap.exists() ? membershipSnap.data() : null

    return isFunPackEffectivelyEnabled(wsData, {
      role: membershipData?.role,
      effectiveControls: membershipData?.effectiveControls,
    })
  } catch (error) {
    console.warn('[isFunPackEnabledForActor] Error checking actor fun pack access:', error)
    return true
  }
}
